import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ASSETS_BASE_URL } from "../config";
import { useOptions } from "../context/optionContext";
import { AuthPageStyles } from "../utilities/themeColors";

const normalizePath = (value, fallback, { allowEmpty = false } = {}) => {
	if (allowEmpty && (value === "" || value === null)) return "";
	const str = typeof value === "string" && value.trim().length ? value.trim() : fallback;
	if (!str) return fallback;
	return str.startsWith("/") ? str : `/${str}`;
};

const useAvailableStableLogoPath = (value, stablePath, fallbackPath) => {
	const configuredPath = normalizePath(value, fallbackPath);
	const stableBase = stablePath.replace(/\.png$/i, "");
	const stablePattern = new RegExp(`^${stableBase}\\.(png|jpe?g|svg)$`, "i");
	const stableCandidate = stablePattern.test(configuredPath) ? configuredPath : stablePath;
	const configuredFallback = stablePattern.test(configuredPath) ? fallbackPath : configuredPath;
	const [resolvedPath, setResolvedPath] = useState(configuredFallback);

	useEffect(() => {
		let active = true;
		setResolvedPath(configuredFallback);
		const image = new Image();
		image.onload = () => active && setResolvedPath(stableCandidate);
		image.onerror = () => active && setResolvedPath(configuredFallback);
		image.src = `${ASSETS_BASE_URL}${stableCandidate}`;
		return () => {
			active = false;
		};
	}, [stableCandidate, configuredFallback]);

	return resolvedPath;
};

const buildUrl = (path) => {
	if (!path) return null;
	return `${ASSETS_BASE_URL}${path}`;
};

const buildAuthBackdropStyle = (url) => ({
	...AuthPageStyles,
	backgroundImage: url
		? `
			linear-gradient(rgba(0, 0, 0, 0.5), rgba(0, 0, 0, 0.5)),
			url('${url}')
		`
		: "none",
});

const buildHomeBackdropStyle = (url) => ({
	backgroundImage: url ? `url('${url}')` : "none",
	backgroundSize: "cover",
	backgroundPosition: "center",
});

const isEnabled = (value, defaultValue = true) => {
	if (value === undefined || value === null) return defaultValue;
	if (typeof value === "boolean") return value;
	return value !== "false" && value !== "0";
};

const AUTH_BACKDROP_OPTION_MAP = [
	{ optionKey: "authBackdropLoginEnabled", match: (path) => path.startsWith("/login") },
	{ optionKey: "authBackdropRegisterEnabled", match: (path) => path.startsWith("/register") },
	{ optionKey: "authBackdropForgotPasswordEnabled", match: (path) => path.startsWith("/forgot-password") },
	{ optionKey: "authBackdropResetPasswordEnabled", match: (path) => path.startsWith("/reset-password") },
	{ optionKey: "authBackdropVerifyEmailEnabled", match: (path) => path.startsWith("/verify-email") },
	{ optionKey: "authBackdropAcceptEulaEnabled", match: (path) => path.startsWith("/accept-eula") },
	{ optionKey: "authBackdropLogoutEnabled", match: (path) => path.startsWith("/logout") || path.startsWith("/cerrar") },
	{ optionKey: "authBackdropTrainingEnabled", match: (path) => path.startsWith("/training") },
	{ optionKey: "authBackdropPVMDashboardEnabled", match: (path) => path.startsWith("/service/digital-solutions/preventive-dashboard") },
	{ optionKey: "authBackdropPMDashboardEnabled", match: (path) => path.startsWith("/service/digital-solutions/predictive-dashboard") },
	{ optionKey: "authBackdropSOSDashboardEnabled", match: (path) => path.startsWith("/service/digital-solutions/sustainable-oven-service-dashboard") },
	{ optionKey: "authBackdropMachineDashboardEnabled", match: (path) => path.startsWith("/service/digital-solutions/machine-dashboard") },
];

const useBrandingAssets = () => {
	const { optionKeys, optionsLoading } = useOptions();
	const location = useLocation();

	const primaryLogoPath = useAvailableStableLogoPath(optionKeys?.primaryLogoPath, "/branding/primaryLogo.png", "/logo.png");
	const secondaryLogoPath = useAvailableStableLogoPath(optionKeys?.secondaryLogoPath, "/branding/footerSecondaryLogo.png", "/sublogo.png");
	const footerPrimaryFallback = normalizePath(optionKeys?.primaryLogoPath, "/logo.png");
	const footerPrimaryLogoPath = useAvailableStableLogoPath(optionKeys?.footerPrimaryLogoPath, "/branding/footerPrimaryLogo.png", footerPrimaryFallback);
	const iconPath = useAvailableStableLogoPath(optionKeys?.siteIconPath, "/branding/primaryFavicon.png", "/logo.png");

	const branding = useMemo(() => {
		const authBackdropPath = normalizePath(optionKeys?.authBackdropPath, "/home_bg2.jpg", { allowEmpty: true });
		const homeBackdropPath = normalizePath(optionKeys?.homeBackdropPath, "/home_bg3.jpg", { allowEmpty: true });
		const stockImagePath = normalizePath(optionKeys?.stockImagePath, "/stock.png");

		const primaryLogoUrl = buildUrl(primaryLogoPath);
		const secondaryLogoUrl = buildUrl(secondaryLogoPath);
		const footerPrimaryLogoUrl = buildUrl(footerPrimaryLogoPath);
		const iconUrl = buildUrl(iconPath);
		const authBackdropUrl = authBackdropPath ? buildUrl(authBackdropPath) : null;
		const homeBackdropUrl = homeBackdropPath ? buildUrl(homeBackdropPath) : null;
		const stockImageUrl = buildUrl(stockImagePath);

		const currentPath = (location?.pathname || "").toLowerCase();
		const authOption = AUTH_BACKDROP_OPTION_MAP.find((item) => item.match(currentPath));
		const authBackdropEnabled = isEnabled(
			authOption ? optionKeys?.[authOption.optionKey] : undefined,
			true
		);
		const homeBackdropEnabled = isEnabled(optionKeys?.homeBackdropEnabled, true);

		return {
			primaryLogoPath,
			secondaryLogoPath,
			footerPrimaryLogoPath,
			iconPath,
			authBackdropPath,
			homeBackdropPath,
			stockImagePath,
			primaryLogoUrl,
			secondaryLogoUrl,
			footerPrimaryLogoUrl,
			iconUrl,
			authBackdropUrl,
			homeBackdropUrl,
			stockImageUrl,
			authBackdropStyle: buildAuthBackdropStyle(authBackdropEnabled ? authBackdropUrl : null),
			homeBackdropStyle: buildHomeBackdropStyle(homeBackdropEnabled ? homeBackdropUrl : null),
			footerLogoEnabled: isEnabled(optionKeys?.footerLogoEnabled, true),
			isReady: !optionsLoading,
		};
	}, [location?.pathname, optionKeys, optionsLoading, primaryLogoPath, secondaryLogoPath, footerPrimaryLogoPath, iconPath]);

	useEffect(() => {
		if (!branding.isReady || !branding.iconUrl) return;
		const setLink = (rel) => {
			const existing = document.querySelector(`link[rel='${rel}']`) || document.createElement("link");
			existing.rel = rel;
			existing.href = branding.iconUrl;
			if (!existing.parentNode) {
				document.head.appendChild(existing);
			}
		};
		setLink("icon");
		setLink("shortcut icon");
	}, [branding.iconUrl, branding.isReady]);

	return branding;
};

export default useBrandingAssets;
